import { uniq } from 'underscore';

import { GetProjects } from '@/connection/projects/get-projects';
import { RootState } from '@/store/main';
import { serializeError } from '@/store/utils/serialize-error';
import { SelectedBrandliveEvent } from '@/types/projects.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { ProjectsReducerCreator } from '../types';
import { EToastTypes } from '@ui/toast/toast.types';

const loadProjectsListForSeriesReducer = (create: ProjectsReducerCreator) => {
  return create.asyncThunk(
    async (
      {
        channel,
      }: {
        channel: number;
      },
      { rejectWithValue, getState }
    ) => {
      try {
        const rootState = getState() as RootState;
        const projectsState = rootState.projects;
        const results: Awaited<ReturnType<typeof GetProjects>> = await GetProjects({
          channel,
          limit: projectsState.getProjectsListForSeriesOptions.pageSize,
          offset: projectsState.getProjectsListForSeriesOptions.offset,
          sort: projectsState.getProjectsListForSeriesOptions.sort,
          search: projectsState.getProjectsListForSeriesOptions.searchCriteria,
          selectedEventGroupUuid: projectsState.selectedProjectGroup,
          time: projectsState.getProjectsListForSeriesOptions.time,
          templateEvents: false,
          excludeTestEvents: false,
        });

        const projects = results.events || [];
        const totals = results.totals || 0;

        return { projects, totals };
      } catch (e) {
        return rejectWithValue(serializeError(e));
      }
    },
    {
      fulfilled: (state, action) => {
        const projects = action.payload.projects as unknown as SelectedBrandliveEvent[];
        if (!state.getProjectsListForSeriesOptions.append) {
          state.projectsListForSeries = projects;
          state.projectsListForSeriesMap = Object.fromEntries(projects.map((proj) => [proj.uuid, proj]));
        } else {
          state.projectsListForSeries = uniq([...state.projectsListForSeries, ...projects], (project) => project.uuid);
          projects.forEach((proj) => {
            state.projectsListForSeriesMap[proj.uuid] = proj;
          });
        }
        state.projectsError = false;
      },
      pending: (state) => {
        state.projectsLoading = true;
      },
      rejected: (state, action) => {
        console.error(action.payload || action.error);
        state.projectsError = true;
        toastTrigger('There was an error loading projects', {
          type: EToastTypes.error,
          autoClose: 10000,
          description: 'Please refresh the page',
        });
      },
      settled: (state) => {
        state.projectsLoading = false;
      },
    }
  );
};

export default loadProjectsListForSeriesReducer;
